/**
 * Site nav auth blocks + user dropdown lifecycle.
 *
 * Two slots in the page chrome show different auth states:
 *   - `#nav-auth-block` — desktop nav top-right: user dropdown when authed,
 *     "Sign in" button when not, empty inside TG Mini App (auto-auth)
 *   - `#mobile-menu-auth` — burger menu mirror: simpler 2-link version
 *
 * Both re-render on every Auth state change via `Auth.onChange`.
 *
 * The desktop dropdown's open/close, click-outside, link-also-closes, and
 * Escape-to-close behaviors are wired with a single document-level click
 * handler — registered as a side effect of importing this module.
 *
 * Originally inline in `main.ts` lines 287-391. Phase 2.3 split 2026-05-12.
 */
import { Auth } from './auth';
import { escapeHtml } from './utils';
import { t } from './i18n';
import { isInTelegram } from './telegram-webapp';

/**
 * Render the nav auth block based on current auth state. Subscribed to
 * Auth.onChange so it stays in sync after login/logout.
 */
export function renderNavAuth(): void {
  const block = document.getElementById('nav-auth-block');
  if (!block) return;
  const user = Auth.getUser();
  if (user) {
    // SECURITY: user.name comes from Google name / Telegram first+last /
    // dev-login displayName — all user-controlled. Always escape.
    const safeName = escapeHtml(user.name ?? '');
    const initials = (user.name ?? '').split(/\s+/).map((s) => s[0] ?? '').join('').slice(0, 2).toUpperCase() || 'U';
    // split() never returns empty array for non-empty input, but TS can't
    // narrow that — coalesce to keep escapeHtml happy.
    const firstName = escapeHtml((user.name ?? 'User').split(/\s+/)[0] ?? 'User');
    block.innerHTML = `
      <div class="nav__user-menu" data-user-menu>
        <button class="nav__user" type="button" data-action="toggle-user-menu" aria-expanded="false" aria-haspopup="true" title="${safeName}">
          <span class="nav__user-avatar">${escapeHtml(initials)}</span>
          <span class="nav__user-name">${firstName}</span>
          <span class="nav__user-chevron" aria-hidden="true">▾</span>
        </button>
        <div class="nav__user-dropdown" hidden>
          <a class="nav__user-item" href="/dashboard/esims" data-i18n="nav.myEsims">${escapeHtml(t('nav.myEsims'))}</a>
          <a class="nav__user-item" href="/dashboard/history" data-i18n="nav.history">${escapeHtml(t('nav.history'))}</a>
          <a class="nav__user-item" href="/dashboard/settings" data-i18n="nav.settings">${escapeHtml(t('nav.settings'))}</a>
          <button class="nav__user-item nav__user-item--danger" type="button" data-action="logout" data-i18n="nav.signOut">${escapeHtml(t('nav.signOut'))}</button>
        </div>
      </div>`;
  } else if (isInTelegram()) {
    // Inside Telegram Mini App: auto-auth in flight or just failed. Don't
    // surface a "Sign in" button — the WebApp context already authenticated
    // the user via initData; if it didn't, a manual prompt won't help (their
    // TG account is the auth source).
    block.innerHTML = '';
  } else {
    // data-i18n is required, not decorative: this button REPLACES the one from
    // index.html, and applyTranslations() only re-translates [data-i18n] nodes.
    // Without it the button freezes in whatever locale was active when Auth
    // last fired, so switching language left a Spanish "Iniciar sesión" on a
    // Turkish page until a full reload (operator-reported 2026-07-27).
    block.innerHTML = `<button class="btn btn--accent btn--sm" data-action="open-login" data-i18n="nav.signIn">${escapeHtml(t('nav.signIn'))}</button>`;
  }
}

/** Mobile menu auth block — same logic, separate slot. */
export function renderMobileMenuAuth(): void {
  const block = document.getElementById('mobile-menu-auth');
  if (!block) return;
  const user = Auth.getUser();
  if (user) {
    block.innerHTML = `
      <a href="/dashboard/esims" class="mobile-menu__link" data-i18n="nav.myAccount">${escapeHtml(t('nav.myAccount'))}</a>
      <button class="mobile-menu__link" data-action="logout" type="button" data-i18n="nav.signOut">${escapeHtml(t('nav.signOut'))}</button>`;
  } else if (isInTelegram()) {
    block.innerHTML = '';
  } else {
    block.innerHTML = `<button class="mobile-menu__link" data-action="open-login" type="button" data-i18n="nav.signIn">${escapeHtml(t('nav.signIn'))}</button>`;
  }
}

// User dropdown lifecycle — open/close + click-outside-to-close +
// click-inside-link-also-closes.
//
// Operator caught 2026-05-08: dropdown stayed open after clicking a link
// like "My eSIMs" inside it, because the click-outside check `closest(
// '[data-user-menu]')` returned truthy (the link IS inside the menu),
// so the close branch was skipped. With SPA routing the page never
// fully reloads, so the dropdown sat open until the next click outside.
// Fix: also close on any link/button inside the dropdown.
const closeUserDropdowns = (): void => {
  document.querySelectorAll<HTMLElement>('.nav__user-dropdown').forEach((d) => {
    d.setAttribute('hidden', '');
  });
  document.querySelectorAll<HTMLElement>('[data-action="toggle-user-menu"]').forEach((t) => {
    t.setAttribute('aria-expanded', 'false');
  });
};
document.addEventListener('click', (e) => {
  const target = e.target as Element | null;
  const toggle = target?.closest<HTMLElement>('[data-action="toggle-user-menu"]');
  if (toggle) {
    e.preventDefault();
    const menu = toggle.closest<HTMLElement>('[data-user-menu]');
    const dropdown = menu?.querySelector<HTMLElement>('.nav__user-dropdown');
    if (dropdown) {
      const willOpen = dropdown.hasAttribute('hidden');
      dropdown.toggleAttribute('hidden', !willOpen);
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    }
    return;
  }
  // Click on a menu item INSIDE the dropdown (link/button) → close + let
  // the action propagate (don't preventDefault).
  const menuItem = target?.closest<HTMLElement>('.nav__user-item');
  if (menuItem) {
    closeUserDropdowns();
    return;
  }
  // Click outside the menu entirely → close.
  if (!target?.closest('[data-user-menu]')) {
    closeUserDropdowns();
  }
});
// Escape closes the dropdown — standard a11y pattern.
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeUserDropdowns();
});

// FIX a11y: toggle burger aria-expanded when mobile menu opens / closes.
// MutationObserver because the menu's class is toggled via the central
// click dispatcher in `actions/index.ts` (toggle-mobile-menu handler).
(function syncBurgerAria() {
  const burger = document.getElementById('nav-burger');
  const menu = document.getElementById('mobile-menu');
  if (!burger || !menu) return;
  const sync = (): void => {
    const open = menu.classList.contains('open') || menu.classList.contains('is-open');
    burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  };
  new MutationObserver(sync).observe(menu, { attributes: true, attributeFilter: ['class'] });
  sync();
})();

// Auto-subscribe to Auth changes so the nav blocks re-render on login/logout.
Auth.onChange(renderNavAuth);
Auth.onChange(renderMobileMenuAuth);
